import {
  Card,
  CardArtwork,
  CardDetails,
  CardOffer,
  CardPrinting,
  CardSelection,
  CatalogCard,
  CatalogPage,
  CatalogPreview,
  CollectionCatalogCard,
  CollectionCatalogPage,
  Game,
  LandingCatalogCard,
  LandingCatalogSection,
  SimplifiedCard,
} from '@lib/types';

export interface CardArtworkDTO {
  id: string;
  imageUrl: string;
  isPrimary: boolean;
}

export interface GameDTO {
  id: string;
  name: string;
  slug: string;
}

export interface SimplifiedCardDTO {
  id: string;
  slug: string;
  namePt?: string;
  nameEn: string;
  preReleaseUntil?: string;
  game: GameDTO;
  primaryArtwork: CardArtworkDTO | null;
}

export interface CardPrintingDTO {
  id: string;
  code: string;
  is_alternate_art?: boolean;
  isAlternateArt?: boolean;
  sort_order?: number;
  sortOrder?: number;
  collection?: { id: string; name: string; code: string; gameId: string };
  collectionId?: string;
  rarity: { id: string; name: string; code: string };
  artwork: CardArtworkDTO | null;
  card?: SimplifiedCardDTO | null;
}

export interface CardSelectionDTO extends SimplifiedCardDTO {
  printings: CardPrintingDTO[];
}

export interface CatalogCardDTO extends SimplifiedCardDTO {
  printingId?: string;
  displayArtwork?: CardArtworkDTO | null;
  availableQuantity: number;
  offeredEditionCount?: number;
  offerCount: number;
  minimumPrice: number | null;
  originalMinimumPrice?: number;
  promotionEndsAt?: string;
  displayRarity?: CardPrintingDTO['rarity'] | null;
  displayCollection?: CardPrintingDTO['collection'] | null;
  collections?: NonNullable<CardPrintingDTO['collection']>[];
  printings?: CardPrintingDTO[];
}

export interface CatalogPageDTO {
  items: CatalogCardDTO[];
  nextCursor?: string;
  totalAvailableQuantity: number;
  totalCards: number;
  appliedFilters: CatalogPage['appliedFilters'];
}

export interface CatalogPreviewDTO {
  id: string;
  slug: string;
  namePt?: string;
  nameEn: string;
  imageUrl: string | null;
}

export interface LandingCatalogSectionDTO {
  game: GameDTO;
  cards: Array<{
    id: string;
    slug: string;
    namePt?: string;
    nameEn: string;
    primaryArtwork: CardArtworkDTO | null;
    latestOfferCreatedAt: string;
    availableQuantity: number;
    offerCount: number;
    minimumPrice: string;
  }>;
}

export interface CardDetailsDTO {
  card: CardSelectionDTO & {
    descriptionPt?: string;
    descriptionEn?: string;
    gameDetails?: Card['gameDetails'];
    availableQuantity: number;
    offerCount: number;
    minimumPrice: number | null;
    originalMinimumPrice?: number;
    promotionEndsAt?: string;
    artworks: CardArtworkDTO[];
  };
  offers: CardOffer[];
  minimumPrice: number | null;
  originalMinimumPrice?: number;
  promotionEndsAt?: string;
  averagePrice: number | null;
  recentSales: {
    lastSoldPrice?: number | null;
    averageUnitPrice: number | null;
    unitSales: number;
  };
}

export interface CollectionCatalogPageDTO {
  items: Array<
    CardSelectionDTO & {
      availableQuantity: number;
      offerCount: number;
      minimumPrice: number | null;
      originalMinimumPrice?: number;
      promotionEndsAt?: string;
      averagePrice: number | null;
    }
  >;
  nextCursor?: string;
}

const createGame = (dto: GameDTO): Game => ({
  id: dto.id,
  name: dto.name,
  slug: dto.slug,
});

export const createCardArtwork = (dto: CardArtworkDTO): CardArtwork => ({
  id: dto.id,
  imageUrl: dto.imageUrl,
  isPrimary: dto.isPrimary,
});

export const createSimplifiedCard = (
  dto: SimplifiedCardDTO,
): SimplifiedCard => ({
  id: dto.id,
  slug: dto.slug,
  namePt: dto.namePt,
  nameEn: dto.nameEn,
  preReleaseUntil: dto.preReleaseUntil,
  game: { ...dto.game },
  primaryArtwork: dto.primaryArtwork
    ? createCardArtwork(dto.primaryArtwork)
    : null,
});

export const createCardPrinting = (dto: CardPrintingDTO): CardPrinting => ({
  id: dto.id,
  code: dto.code,
  isAlternateArt: dto.isAlternateArt ?? dto.is_alternate_art ?? false,
  is_alternate_art: dto.is_alternate_art ?? dto.isAlternateArt ?? false,
  sortOrder: dto.sortOrder ?? dto.sort_order ?? 0,
  sort_order: dto.sort_order ?? dto.sortOrder ?? 0,
  collection: dto.collection!,
  collectionId: dto.collectionId,
  rarity: dto.rarity,
  artwork: dto.artwork ? createCardArtwork(dto.artwork) : null,
  card: dto.card ? createSimplifiedCard(dto.card) : null,
});

export const createCardSelection = (dto: CardSelectionDTO): CardSelection => ({
  ...createSimplifiedCard(dto),
  printings: dto.printings.map(createCardPrinting),
});

export const createCatalogCard = (dto: CatalogCardDTO): CatalogCard => ({
  ...createSimplifiedCard(dto),
  printingId: dto.printingId,
  displayArtwork: dto.displayArtwork
    ? createCardArtwork(dto.displayArtwork)
    : undefined,
  availableQuantity: dto.availableQuantity,
  offeredEditionCount: dto.offeredEditionCount,
  offerCount: dto.offerCount,
  minimumPrice: dto.minimumPrice,
  originalMinimumPrice: dto.originalMinimumPrice,
  promotionEndsAt: dto.promotionEndsAt,
  preReleaseUntil: dto.preReleaseUntil,
  displayRarity: dto.displayRarity
    ? { ...dto.displayRarity }
    : dto.displayRarity,
  displayCollection: dto.displayCollection
    ? { ...dto.displayCollection }
    : dto.displayCollection,
  collections: dto.collections
    ? dto.collections.map((collection) => ({ ...collection }))
    : undefined,
  printings: dto.printings ? dto.printings.map(createCardPrinting) : undefined,
});

export const createCatalogPage = (dto: CatalogPageDTO): CatalogPage => ({
  items: dto.items.map(createCatalogCard),
  nextCursor: dto.nextCursor,
  totalAvailableQuantity: dto.totalAvailableQuantity,
  totalCards: dto.totalCards,
  appliedFilters: { ...dto.appliedFilters },
});

export const createCatalogPreview = (
  dto: CatalogPreviewDTO,
): CatalogPreview => ({ ...dto });

const createLandingCatalogCard = (
  dto: LandingCatalogSectionDTO['cards'][number],
): LandingCatalogCard => ({
  ...dto,
  primaryArtwork: dto.primaryArtwork
    ? createCardArtwork(dto.primaryArtwork)
    : null,
});

export const createLandingCatalogSection = (
  dto: LandingCatalogSectionDTO,
): LandingCatalogSection => ({
  game: createGame(dto.game),
  cards: dto.cards.map(createLandingCatalogCard),
});

export const createCardDetails = (dto: CardDetailsDTO): CardDetails => ({
  card: {
    ...createCardSelection(dto.card),
    descriptionPt: dto.card.descriptionPt,
    descriptionEn: dto.card.descriptionEn,
    gameDetails: dto.card.gameDetails,
    availableQuantity: dto.card.availableQuantity,
    offerCount: dto.card.offerCount,
    minimumPrice: dto.card.minimumPrice,
    originalMinimumPrice: dto.card.originalMinimumPrice,
    promotionEndsAt: dto.card.promotionEndsAt,
  },
  offers: dto.offers.map((offer) => ({
    ...offer,
    printing: createCardPrinting(offer.printing),
    store: {
      ...offer.store,
      shippingMethods: offer.store.shippingMethods.map((method) => ({
        ...method,
      })),
    },
  })),
  minimumPrice: dto.minimumPrice,
  originalMinimumPrice: dto.originalMinimumPrice,
  promotionEndsAt: dto.promotionEndsAt,
  averagePrice: dto.averagePrice,
  recentSales: {
    ...dto.recentSales,
    lastSoldPrice: dto.recentSales.lastSoldPrice ?? null,
  },
});

const createCollectionCatalogCard = (
  dto: CollectionCatalogPageDTO['items'][number],
): CollectionCatalogCard => ({
  ...createCardSelection(dto),
  availableQuantity: dto.availableQuantity,
  offerCount: dto.offerCount,
  minimumPrice: dto.minimumPrice,
  originalMinimumPrice: dto.originalMinimumPrice,
  promotionEndsAt: dto.promotionEndsAt,
  averagePrice: dto.averagePrice,
});

export const createCollectionCatalogPage = (
  dto: CollectionCatalogPageDTO,
): CollectionCatalogPage => ({
  items: dto.items.map(createCollectionCatalogCard),
  nextCursor: dto.nextCursor,
});

export interface AdminCollectionDetailsDTO {
  id: string;
  gameId: string;
  name: string;
  code: string;
  preReleaseDate: string | null;
  releaseDate: string | null;
  cardCount: number;
  numOfCards: number | null;
  providerId: string | null;
  marketplaceVisible: boolean;
  game: GameDTO;
  printings: Array<{
    id: string;
    code: string;
    active: boolean;
    isAlternateArt: boolean;
    sortOrder: number;
    collection: { id: string; name: string; code: string; gameId: string };
    rarity: { id: string; name: string; code: string };
    artwork: CardArtworkDTO | null;
    card: { id: string; nameEn: string; namePt: string | null };
  }>;
}
